import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { cities, CityData, isCityNew } from "@/data/cities";
import { MapPin, Search, ArrowRight, Sparkles } from "lucide-react";

const CityDirectory = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredCities = useMemo(() => {
    if (!searchTerm.trim()) return cities;
    const lower = searchTerm.toLowerCase();
    return cities.filter(
      (c) =>
        c.name.toLowerCase().includes(lower) ||
        c.providers.some((p) => p.name.toLowerCase().includes(lower))
    );
  }, [searchTerm]);

  return (
    <section
      className="py-12 md:py-16 bg-muted/20 border-t border-border"
      id="staedte-uebersicht"
      aria-label="Alle Plasmaspende-Standorte in Deutschland"
    >
      <div className="container container-narrow">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-1.5 mb-3 text-xs font-semibold text-primary">
              <MapPin className="w-3.5 h-3.5" />
              <span>Bundesweites Zentren-Verzeichnis</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold font-display text-foreground">
              Alle Standorte von <span className="text-gradient">A bis Z</span>
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mt-1">
              Finden Sie Ihr Plasmazentrum vor Ort und sichern Sie sich bis zu 150 € Anmeldebonus.
            </p>
          </div>

          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Stadt suchen (z. B. Hamburg, Köln, Berlin)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Stadt nach Namen filtern"
              className="w-full bg-background border border-border rounded-xl pl-9 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
            />
          </div>
        </div>

        {filteredCities.length === 0 ? (
          <div className="text-center py-12 card-modern p-6">
            <p className="text-muted-foreground">Keine Standorte für „{searchTerm}“ gefunden.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filteredCities.map((city: CityData) => {
              const maxBonus = city.providers.reduce((max, p) => {
                const val = parseInt(p.maxBonus);
                return val > max ? val : max;
              }, 0);
              const hasCSL = city.providers.some((p) => p.name.includes("CSL"));
              const hasPSE = city.providers.some((p) => p.name.includes("Plasma Service"));
              const isNew = isCityNew(city);

              return (
                <Link
                  key={city.slug}
                  to={`/${city.slug}`}
                  title={`Plasmaspenden in ${city.name} - Bis zu ${maxBonus} € Bonus`}
                  className={`card-modern p-4 hover:border-primary/50 hover:bg-card/90 transition-all group flex flex-col justify-between ${
                    isNew ? "border-emerald-500/40 shadow-[0_0_20px_-5px_rgba(16,185,129,0.2)]" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h4 className="font-bold text-foreground group-hover:text-primary transition-colors text-base font-display flex items-center gap-1.5 flex-wrap">
                        <MapPin className="w-4 h-4 text-primary shrink-0" />
                        <span>{city.name}</span>
                        {isNew && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full shadow-sm animate-pulse motion-reduce:animate-none">
                            <Sparkles className="w-2.5 h-2.5 text-emerald-400" />
                            Neu
                          </span>
                        )}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {city.providers.map((p) => p.name).join(" & ")}
                      </p>
                    </div>

                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-bonus bg-bonus/10 border border-bonus/20 px-2 py-0.5 rounded-full shrink-0">
                      <Sparkles className="w-3 h-3" />
                      Bis {maxBonus} €
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-border/50 text-xs mt-2">
                    <div className="flex items-center gap-1.5">
                      {hasCSL && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary border border-primary/20">
                          CSL
                        </span>
                      )}
                      {hasPSE && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent/10 text-accent border border-accent/20">
                          PSE
                        </span>
                      )}
                    </div>

                    <span className="text-muted-foreground group-hover:text-foreground flex items-center gap-1 font-medium transition-colors">
                      Details & Spendernummer
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default CityDirectory;
